﻿/**
 * 用户设置 API 服务 - 后端 HTTP API 调用实现
 *
 * 通过 VITE_USE_MOCK 环境变量切换 Mock / 后端 API：
 * - 'true' 时使用本地 Mock 数据作为开发回退
 * - 默认（false）调用真实后端接口
 */

import { httpClient, httpGet, httpPost, httpDelete } from '@/utils/httpClient'
import { wrapResponse, type ApiResponse, type PaginatedResponse } from '@/services/types'
import type { UserRole } from '@/types/auth'
import type { OrganizationType } from '@/types/organization'
import { FeedbackCategoryLabels, AdminStatusToUserStatus, type FeedbackStatus } from '@/types/user-settings'
import type {
  UserProfile,
  UpdateProfileDto,
  ChangePasswordDto,
  ResetPasswordByCodeDto,
  PasswordPolicy,
  OrgMembershipApplication,
  ApplyForOrgDto,
  LoginDevice,
  FeedbackTicket,
  CreateFeedbackDto,
  FaqItem,
  AccountDeletionRequest,
  WechatBindStatus,
  ExtendedUserAccountStatus,
  ThemePreference,
  FontSizePreference,
  OrgApplicationStatus,
  DeviceType,
  DeletionStatus,
  DeletionCheckResult,
  RequestDeletionDto,
  CancelDeletionDto,
} from '@/types/user-settings'

const BASE = '/api/user/settings'

// =================== Backend Response Types ===================

/** 后端分页结果格式 */
interface BackendPageResult<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

// =================== Backend Data Types (replace `any`) ===================

interface BackendUserProfile {
  id: string
  mobile: string
  email: string | null
  avatarUrl: string | null
  displayName: string
  realName: string | null
  region: string | null
  wechatOpenId: string | null
  wechatBound: boolean
  hasPassword: boolean
  organizationId: string | null
  organizationName: string | null
  organizationType: string | null
  role: string
  status: string
  theme: string | null
  fontSize: string | null
  createdAt: string
  lastLoginAt: string
}

interface BackendOrgApplication {
  id: string
  applicantId: string
  applicantName: string
  targetOrgId: string
  targetOrgName: string
  targetOrgType: string
  reason: string
  status: string
  reviewerId: string | null
  reviewerName: string | null
  reviewedAt: string | null
  rejectReason: string | null
  createdAt: string
  updatedAt: string
}

interface BackendLoginDevice {
  id: string
  userId: string
  deviceName: string
  deviceType: string
  browser: string | null
  os: string | null
  ipAddress: string
  location: string | null
  loginAt: string
  lastActiveAt: string
  isCurrentDevice: boolean
  sessionStatus: string
}

interface BackendFeedbackTicket {
  id: string
  feedbackNo?: string
  userId: string
  type: string
  typeLabel: string
  subject: string
  description: string
  screenshots: string[] | string | null
  status: string
  reply: string | null
  handlerName?: string | null
  closeReason?: string | null
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  repliedAt: string | null
  closedAt: string | null
}

interface BackendFaqItem {
  id: string
  category: string
  question: string
  answer: string
  order: number
}

interface BackendDeletionRequest {
  id: string
  userId: string
  reason: string | null
  status: string
  requestedAt: string
  coolingDownEndsAt: string | null
  completedAt: string | null
  cancelledAt: string | null
}

/** 后端 DeletionCheckResponse：activeRequest 为进行中的注销申请（无则缺省/null） */
interface BackendDeletionCheck {
  canDelete: boolean
  reasons: string[]
  activeRequest?: BackendDeletionRequest | null
}

interface BackendWechatBindStatus {
  bound: boolean
  nickname: string
}

// =================== Response Adapters ===================

function parseDate(dateStr?: string | null): Date | undefined {
  if (!dateStr) return undefined
  const d = new Date(dateStr)
  return isNaN(d.getTime()) ? undefined : d
}

// =================== Data Adapters ===================

function adaptProfile(raw: BackendUserProfile): UserProfile {
  return {
    id: raw.id ?? '',
    mobile: raw.mobile ?? '',
    email: raw.email ?? undefined,
    avatarUrl: raw.avatarUrl ?? undefined,
    displayName: raw.displayName,
    realName: raw.realName ?? undefined,
    region: raw.region ?? undefined,
    wechatOpenId: raw.wechatOpenId ?? undefined,
    wechatBound: raw.wechatBound ?? false,
    hasPassword: raw.hasPassword,
    organizationId: raw.organizationId ?? undefined,
    organizationName: raw.organizationName ?? undefined,
    organizationType: (raw.organizationType as OrganizationType) ?? undefined,
    role: (raw.role as UserRole) ?? 'standard',
    status: (raw.status as ExtendedUserAccountStatus) ?? 'normal',
    theme: (raw.theme as ThemePreference) ?? 'system',
    fontSize: (raw.fontSize as FontSizePreference) ?? 'normal',
    createdAt: parseDate(raw.createdAt) ?? new Date(),
    lastLoginAt: parseDate(raw.lastLoginAt) ?? new Date(),
  }
}

function adaptOrgApplication(raw: BackendOrgApplication): OrgMembershipApplication {
  return {
    id: raw.id ?? '',
    applicantId: raw.applicantId ?? '',
    applicantName: raw.applicantName ?? '',
    targetOrgId: raw.targetOrgId ?? '',
    targetOrgName: raw.targetOrgName ?? '',
    targetOrgType: (raw.targetOrgType as OrganizationType) ?? 'town',
    reason: raw.reason ?? '',
    status: (raw.status as OrgApplicationStatus) ?? 'pending',
    reviewerId: raw.reviewerId ?? undefined,
    reviewerName: raw.reviewerName ?? undefined,
    reviewedAt: parseDate(raw.reviewedAt),
    rejectReason: raw.rejectReason ?? undefined,
    createdAt: parseDate(raw.createdAt) ?? new Date(),
    updatedAt: parseDate(raw.updatedAt) ?? new Date(),
  }
}

function adaptDevice(raw: BackendLoginDevice): LoginDevice {
  return {
    id: raw.id ?? '',
    userId: raw.userId ?? '',
    deviceName: raw.deviceName ?? '',
    deviceType: (raw.deviceType as DeviceType) ?? 'web',
    browser: raw.browser ?? undefined,
    os: raw.os ?? undefined,
    ipAddress: raw.ipAddress ?? '',
    location: raw.location ?? undefined,
    loginAt: parseDate(raw.loginAt) ?? new Date(),
    lastActiveAt: parseDate(raw.lastActiveAt) ?? new Date(),
    isCurrentDevice: raw.isCurrentDevice ?? false,
    sessionStatus: (raw.sessionStatus as 'active' | 'terminated') ?? 'active',
  }
}

function adaptFaq(raw: BackendFaqItem): FaqItem {
  return {
    id: raw.id ?? '',
    category: raw.category ?? '',
    question: raw.question ?? '',
    answer: raw.answer ?? '',
    order: raw.order ?? 0,
  }
}

function parseFeedbackScreenshots(raw: string[] | string | null | undefined): string[] | undefined {
  if (raw == null) return undefined
  if (Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

function adaptFeedback(raw: BackendFeedbackTicket): FeedbackTicket {
  const userStatus: FeedbackStatus = AdminStatusToUserStatus[raw.status] || (raw.status as FeedbackStatus) || 'submitted'
  return {
    id: raw.id ?? '',
    feedbackNo: raw.feedbackNo,
    userId: raw.userId ?? '',
    type: raw.type ?? 'feature',
    typeLabel: raw.typeLabel || FeedbackCategoryLabels[raw.type] || raw.type,
    subject: raw.subject ?? '',
    description: raw.description ?? '',
    screenshots: parseFeedbackScreenshots(raw.screenshots),
    status: userStatus,
    createdAt: parseDate(raw.createdAt) ?? new Date(),
    updatedAt: parseDate(raw.updatedAt) ?? new Date(),
    resolvedAt: parseDate(raw.resolvedAt) ?? parseDate(raw.repliedAt),
    repliedAt: parseDate(raw.repliedAt),
    closedAt: parseDate(raw.closedAt),
    reply: raw.reply ?? undefined,
    handlerName: raw.handlerName ?? undefined,
    closeReason: raw.closeReason ?? undefined,
  }
}

function adaptDeletionRequest(raw: BackendDeletionRequest): AccountDeletionRequest {
  return {
    id: raw.id ?? '',
    userId: raw.userId ?? '',
    reason: raw.reason ?? undefined,
    status: (raw.status as DeletionStatus) ?? 'pending',
    requestedAt: parseDate(raw.requestedAt) ?? new Date(),
    coolingDownEndsAt: parseDate(raw.coolingDownEndsAt),
    completedAt: parseDate(raw.completedAt),
    cancelledAt: parseDate(raw.cancelledAt),
  }
}

// =================== API Strategy Pattern ===================

interface UserSettingsApiStrategy {
  getUserProfile(): Promise<ApiResponse<UserProfile>>
  updateUserProfile(dto: UpdateProfileDto): Promise<ApiResponse<UserProfile>>
  uploadAvatar(file: File): Promise<ApiResponse<{ avatarUrl: string }>>
  changePassword(dto: ChangePasswordDto): Promise<ApiResponse<void>>
  getPasswordPolicy(): Promise<ApiResponse<PasswordPolicy>>
  sendResetCode(mobile: string): Promise<ApiResponse<void>>
  resetPasswordByCode(dto: ResetPasswordByCodeDto): Promise<ApiResponse<void>>
  listMyApplications(): Promise<ApiResponse<OrgMembershipApplication[]>>
  applyForOrg(dto: ApplyForOrgDto): Promise<ApiResponse<OrgMembershipApplication>>
  withdrawApplication(id: string): Promise<ApiResponse<void>>
  requestLeaveOrg(dto: { reason?: string }): Promise<ApiResponse<void>>
  listLoginDevices(): Promise<ApiResponse<LoginDevice[]>>
  terminateDevice(id: string): Promise<ApiResponse<void>>
  /** 修复项：#7 退出全部其他设备 —— 行业标准安全功能 */
  terminateAllDevices(): Promise<ApiResponse<{ terminatedCount: number }>>
  getWechatBindStatus(): Promise<ApiResponse<WechatBindStatus>>
  initiateWechatBind(): Promise<ApiResponse<{ authUrl: string; state: string }>>
  confirmWechatBind(code: string, state: string): Promise<ApiResponse<void>>
  unbindWechat(): Promise<ApiResponse<void>>
  listFaqs(category?: string): Promise<ApiResponse<FaqItem[]>>
  submitFeedback(dto: CreateFeedbackDto): Promise<ApiResponse<FeedbackTicket>>
  listMyFeedbacks(page: number, pageSize: number): Promise<PaginatedResponse<FeedbackTicket>>
  checkDeletionPrerequisites(): Promise<ApiResponse<DeletionCheckResult>>
  sendDeletionCode(): Promise<ApiResponse<void>>
  requestAccountDeletion(dto: RequestDeletionDto): Promise<ApiResponse<AccountDeletionRequest>>
  cancelAccountDeletion(dto: CancelDeletionDto): Promise<ApiResponse<void>>
}

function createBackendApi(): UserSettingsApiStrategy {
  return {
    async getUserProfile(): Promise<ApiResponse<UserProfile>> {
      const data = await httpGet<BackendUserProfile>(`${BASE}/profile`)
      return wrapResponse(adaptProfile(data))
    },
    async updateUserProfile(dto: UpdateProfileDto): Promise<ApiResponse<UserProfile>> {
      const response = await httpClient.patch<BackendUserProfile>(`${BASE}/profile`, dto)
      return wrapResponse(adaptProfile(response.data))
    },
    async uploadAvatar(file: File): Promise<ApiResponse<{ avatarUrl: string }>> {
      const formData = new FormData()
      formData.append('file', file)
      const data = await httpPost<{ avatarUrl: string }>(
        `${BASE}/avatar`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return wrapResponse(data)
    },
    async changePassword(dto: ChangePasswordDto): Promise<ApiResponse<void>> {
      await httpPost<void>(`${BASE}/password/change`, dto)
      return wrapResponse(undefined as void)
    },
    async getPasswordPolicy(): Promise<ApiResponse<PasswordPolicy>> {
      const data = await httpGet<PasswordPolicy>(`${BASE}/password-policy`)
      return wrapResponse(data)
    },
    async sendResetCode(mobile: string): Promise<ApiResponse<void>> {
      await httpPost<void>(`${BASE}/password/reset-code`, { mobile })
      return wrapResponse(undefined as void)
    },
    async resetPasswordByCode(dto: ResetPasswordByCodeDto): Promise<ApiResponse<void>> {
      await httpPost<void>(`${BASE}/password/reset`, dto)
      return wrapResponse(undefined as void)
    },
    async listMyApplications(): Promise<ApiResponse<OrgMembershipApplication[]>> {
      const data = await httpGet<BackendOrgApplication[]>(`${BASE}/org-applications`)
      return wrapResponse(data.map(adaptOrgApplication))
    },
    async applyForOrg(dto: ApplyForOrgDto): Promise<ApiResponse<OrgMembershipApplication>> {
      const data = await httpPost<BackendOrgApplication>(`${BASE}/org-applications`, dto)
      return wrapResponse(adaptOrgApplication(data))
    },
    async withdrawApplication(id: string): Promise<ApiResponse<void>> {
      await httpDelete<void>(`${BASE}/org-applications/${id}`)
      return wrapResponse(undefined as void)
    },
    async requestLeaveOrg(dto: { reason?: string }): Promise<ApiResponse<void>> {
      await httpPost<void>(`${BASE}/org-leave`, dto)
      return wrapResponse(undefined as void)
    },
    // P8-fix: 旧版通知偏好接口已删除，通知偏好统一走 services/inbox-notifications（后端
    // /api/user/notification-preferences）。
    async listLoginDevices(): Promise<ApiResponse<LoginDevice[]>> {
      const data = await httpGet<BackendLoginDevice[]>(`${BASE}/devices`)
      return wrapResponse(data.map(adaptDevice))
    },
    async terminateDevice(id: string): Promise<ApiResponse<void>> {
      await httpDelete<void>(`${BASE}/devices/${id}`)
      return wrapResponse(undefined as void)
    },
    /** 修复项：#7 —— 后端批量退出接口（无 path 参数） */
    async terminateAllDevices(): Promise<ApiResponse<{ terminatedCount: number }>> {
      const data = await httpDelete<{ terminatedCount: number }>(`${BASE}/devices`)
      return wrapResponse(data)
    },
    async getWechatBindStatus(): Promise<ApiResponse<WechatBindStatus>> {
      const data = await httpGet<BackendWechatBindStatus>(`${BASE}/wechat-status`)
      return wrapResponse({ bound: data.bound, nickname: data.nickname })
    },
    async initiateWechatBind(): Promise<ApiResponse<{ authUrl: string; state: string }>> {
      const data = await httpPost<{ authUrl: string; state: string }>(`${BASE}/wechat-bind`)
      return wrapResponse(data)
    },
    async confirmWechatBind(code: string, state: string): Promise<ApiResponse<void>> {
      await httpPost<void>(`${BASE}/wechat-bind/confirm`, { code, state })
      return wrapResponse(undefined as void)
    },
    async unbindWechat(): Promise<ApiResponse<void>> {
      await httpDelete<void>(`${BASE}/wechat-bind`)
      return wrapResponse(undefined as void)
    },
    async listFaqs(category?: string): Promise<ApiResponse<FaqItem[]>> {
      const params = category ? { category } : {}
      const data = await httpGet<BackendFaqItem[]>(`${BASE}/faqs`, { params })
      return wrapResponse(data.map(adaptFaq))
    },
    async submitFeedback(dto: CreateFeedbackDto): Promise<ApiResponse<FeedbackTicket>> {
      const data = await httpPost<BackendFeedbackTicket>(`${BASE}/feedback`, dto)
      return wrapResponse(adaptFeedback(data))
    },
    async listMyFeedbacks(page: number, pageSize: number): Promise<PaginatedResponse<FeedbackTicket>> {
      const data = await httpGet<BackendPageResult<BackendFeedbackTicket>>(`${BASE}/feedback`, {
        params: { page, pageSize },
      })
      const pageResp: PaginatedResponse<FeedbackTicket> = {
        items: data.items.map(adaptFeedback),
        total: data.total,
        page: data.page,
        pageSize: data.pageSize,
      }
      return pageResp
    },
    async checkDeletionPrerequisites(): Promise<ApiResponse<DeletionCheckResult>> {
      const data = await httpGet<BackendDeletionCheck>(`${BASE}/deletion-check`)
      return wrapResponse({
        canDelete: data.canDelete,
        reasons: data.reasons ?? [],
        // 刷新恢复关键：进行中的申请随检查结果一并返回
        activeRequest: data.activeRequest ? adaptDeletionRequest(data.activeRequest) : null,
      })
    },
    async sendDeletionCode(): Promise<ApiResponse<void>> {
      await httpPost<void>(`${BASE}/deletion/code`)
      return wrapResponse(undefined as void)
    },
    async requestAccountDeletion(dto: RequestDeletionDto): Promise<ApiResponse<AccountDeletionRequest>> {
      const data = await httpPost<BackendDeletionRequest>(`${BASE}/deletion`, dto)
      return wrapResponse(adaptDeletionRequest(data))
    },
    async cancelAccountDeletion(dto: CancelDeletionDto): Promise<ApiResponse<void>> {
      // DELETE 携带 JSON body（axios delete 走 config.data），后端 @RequestBody(required=false) 接收
      await httpDelete<void>(`${BASE}/deletion`, { data: dto })
      return wrapResponse(undefined as void)
    },
  }
}

async function createMockApi(): Promise<UserSettingsApiStrategy> {
  const mock = await import('@/mock/user-settings')
  return {
    async getUserProfile() { return mock.getUserProfile() },
    async updateUserProfile(dto) { return mock.updateUserProfile(dto) },
    async uploadAvatar(file) { return mock.uploadAvatar(file) },
    async changePassword(dto) { return mock.changePassword(dto) },
    async getPasswordPolicy() { return mock.getPasswordPolicy() },
    async sendResetCode(mobile) { return mock.sendResetCode(mobile) },
    async resetPasswordByCode(dto) { return mock.resetPasswordByCode(dto) },
    async listMyApplications() { return mock.listMyApplications() },
    async applyForOrg(dto) { return mock.applyForOrg(dto) },
    async withdrawApplication(id) { return mock.withdrawApplication(id) },
    async requestLeaveOrg(dto) { return mock.requestLeaveOrg(dto) },
    async listLoginDevices() { return mock.listLoginDevices() },
    async terminateDevice(id) { return mock.terminateDevice(id) },
    /** 修复项：#7 —— mock 端转发到 mock.terminateAllDevices */
    async terminateAllDevices() { return mock.terminateAllDevices() },
    async getWechatBindStatus() { return mock.getWechatBindStatus() },
    async initiateWechatBind() { return mock.initiateWechatBind() },
    async confirmWechatBind(code, state) { return mock.confirmWechatBind(code, state) },
    async unbindWechat() { return mock.unbindWechat() },
    async listFaqs(category?) { return mock.listFaqs(category) },
    async submitFeedback(dto) { return mock.submitFeedback(dto) },
    async listMyFeedbacks(page, pageSize) { return mock.listMyFeedbacks(page, pageSize) },
    async checkDeletionPrerequisites() { return mock.checkDeletionPrerequisites() },
    async sendDeletionCode() { return mock.sendDeletionCode() },
    async requestAccountDeletion(dto) { return mock.requestAccountDeletion(dto) },
    async cancelAccountDeletion(dto) { return mock.cancelAccountDeletion(dto) },
  }
}

// =================== Lazy-initialized strategy ===================

let _strategy: UserSettingsApiStrategy | null = null

async function getStrategy(): Promise<UserSettingsApiStrategy> {
  if (!_strategy) {
    // 基于 VITE_USE_MOCK：不启用 Mock 时使用后端 API，默认走真实 API
    const USE_BACKEND_API = import.meta.env.VITE_USE_MOCK !== 'true'
    _strategy = USE_BACKEND_API ? createBackendApi() : await createMockApi()
  }
  return _strategy
}

// =================== Public API (re-exports) ===================

export async function getUserProfile() { return (await getStrategy()).getUserProfile() }
export async function updateUserProfile(dto: UpdateProfileDto) { return (await getStrategy()).updateUserProfile(dto) }
export async function uploadAvatar(file: File) { return (await getStrategy()).uploadAvatar(file) }
export async function changePassword(dto: ChangePasswordDto) { return (await getStrategy()).changePassword(dto) }
export async function getPasswordPolicy() { return (await getStrategy()).getPasswordPolicy() }
export async function sendResetCode(mobile: string) { return (await getStrategy()).sendResetCode(mobile) }
export async function resetPasswordByCode(dto: ResetPasswordByCodeDto) { return (await getStrategy()).resetPasswordByCode(dto) }
export async function listMyApplications() { return (await getStrategy()).listMyApplications() }
export async function applyForOrg(dto: ApplyForOrgDto) { return (await getStrategy()).applyForOrg(dto) }
export async function withdrawApplication(id: string) { return (await getStrategy()).withdrawApplication(id) }
export async function requestLeaveOrg(dto: { reason?: string }) { return (await getStrategy()).requestLeaveOrg(dto) }
export async function listLoginDevices() { return (await getStrategy()).listLoginDevices() }
export async function terminateDevice(id: string) { return (await getStrategy()).terminateDevice(id) }
export async function terminateAllDevices() { return (await getStrategy()).terminateAllDevices() }
export async function getWechatBindStatus() { return (await getStrategy()).getWechatBindStatus() }
export async function initiateWechatBind(): Promise<ApiResponse<{ authUrl: string; state: string }>> { return (await getStrategy()).initiateWechatBind() }
export async function confirmWechatBind(code: string, state: string) { return (await getStrategy()).confirmWechatBind(code, state) }
export async function unbindWechat() { return (await getStrategy()).unbindWechat() }
export async function listFaqs(category?: string) { return (await getStrategy()).listFaqs(category) }
export async function submitFeedback(dto: CreateFeedbackDto) { return (await getStrategy()).submitFeedback(dto) }
export async function listMyFeedbacks(page = 1, pageSize = 10) { return (await getStrategy()).listMyFeedbacks(page, pageSize) }
export async function checkDeletionPrerequisites() { return (await getStrategy()).checkDeletionPrerequisites() }
export async function sendDeletionCode() { return (await getStrategy()).sendDeletionCode() }
export async function requestAccountDeletion(dto: RequestDeletionDto) { return (await getStrategy()).requestAccountDeletion(dto) }
export async function cancelAccountDeletion(dto: CancelDeletionDto) { return (await getStrategy()).cancelAccountDeletion(dto) }
